import { ChangeDetectionStrategy, Component, inject, input } from '@angular/core';
import {
  DailyFeedbackReportsAggregationData,
  DailyFeedbackReportsContentData
} from "../../types/daily-feedback-reports-page.types";
import { UtilityService } from "../../../../../core/services/utility.service";
import { marketplaces, negativePercentValueType } from "../../configs/daily-feedback-reports-row-config";
import {
  GetQuantityParams,
  MarketplaceKeyType,
  NegativePercentType,
  TotalKeyType,
} from "../../types/daily-feedback-reports-row.types";
import { DailyFeedbackReportsRowService } from "../../services/daily-feedback-reports-row.service";
import {
  DailyFeedbackReportsRatingComponent
} from "../daily-feedback-reports-rating/daily-feedback-reports-rating.component";

@Component({
  selector: 'app-daily-feedback-reports-row',
  standalone: true,
  templateUrl: './daily-feedback-reports-row.component.html',
  styleUrls: [
    '../daily-feedback-reports-page-content/daily-feedback-reports-page-content.component.scss',
    './daily-feedback-reports-row.component.scss'
  ],
  imports: [
    DailyFeedbackReportsRatingComponent
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DailyFeedbackReportsRowComponent {
  isAggregationRow = input<boolean>(false);
  data = input.required<DailyFeedbackReportsContentData | DailyFeedbackReportsAggregationData>();

  private utilityService: UtilityService = inject(UtilityService);
  private dailyFeedbackReportsRowService: DailyFeedbackReportsRowService = inject(DailyFeedbackReportsRowService);

  public readonly marketplaces = marketplaces;
  public readonly negativePercentValueType = negativePercentValueType;

  public getPeriod(): string {
    const data = this.data();
    return this.isAggregationData(data) ?
      `${data.dateFrom} - ${data.dateTo}` :
      data?.date ? this.utilityService.formatArrayToRussianDate(data?.date) : '';
  }

  public getQuantity(type: MarketplaceKeyType, marketplace: string): string {
    const data = this.data();
    const params: GetQuantityParams = {
      quantityType: type,
      marketplace: marketplace
    };

    return this.isAggregationRow() ?
      this.dailyFeedbackReportsRowService.getAggregationQuantity(params) :
      this.dailyFeedbackReportsRowService.getReportQuantity({
        ...params,
        data: !this.isAggregationData(data) ? data : undefined
      });
  }

  public getTotal(type: TotalKeyType): string {
    return this.isAggregationRow() ?
      this.dailyFeedbackReportsRowService.getAggregationTotalValue({ valueType: type }) :
      this.dailyFeedbackReportsRowService.getReportTotalValue({
        valueType: type,
        data: !this.isAggregationData(this.data()) ? this.data() : undefined
      });
  }

  public getNegativePercent(marketplace: string, type: NegativePercentType): string {
    return this.dailyFeedbackReportsRowService.getNegativePercent({
      data: this.data(),
      type: type,
      marketplace: marketplace
    });
  }

  public getIsTotalNegativePercentMismatch(): boolean {
    return this.data()?.isTotalNegativePercentMismatch || false;
  }

  private isAggregationData(data: DailyFeedbackReportsContentData | DailyFeedbackReportsAggregationData): data is DailyFeedbackReportsAggregationData {
    return 'dateFrom' in data && 'dateTo' in data;
  }
}
